import { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  BasicTable,
  Button,
  ContentBox,
  ContentPage,
  DescriptionList,
  NoResults,
  Notice,
  PageActions,
  ProgressBar,
  Skeleton,
  StatePill,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { DomainStatus, escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import { questionsLabel, quizStatusKey, respondentsLabel } from "@helpers/quizForm";
import {
  answersLabel,
  answerText,
  countPercentLabel,
  groupAnswers,
  isMultiple,
  sortQuestions,
  summarizeQuestion,
} from "@helpers/quizAnswers";

// Respostas de um questionário (modelo Detalhe do DS OneWork, sem abas), só do ADM.
// Uma caixa por pergunta: a múltipla escolha com a contagem por opção, a escrita com
// a tabela das respostas. As respostas antigas sem pergunta ficam numa caixa no fim.

const LIST_HREF = "/c/quiz";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// Opção: a barra ocupa a linha; "Correta" fica numa coluna fixa à direita
const OptionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 80px;
  align-items: end;
  column-gap: 16px;
`;

// Resposta escrita com as quebras de linha de quem escreveu
const Answer = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

function AnswersSkeleton() {
  return (
    <ContentPage aria-busy="true">
      <ContentBox>
        <Skeleton $height={90} />
      </ContentBox>
      <ContentBox>
        <Skeleton $height={200} />
      </ContentBox>
    </ContentPage>
  );
}

const answerColumns = [
  {
    id: "person",
    header: "Pessoa",
    size: 260,
    cell: ({ row }) => {
      const user = row.original.user;
      return (
        <Heading>
          <Text $text={escapeHtml(user?.name || "Pessoa removida")} $variant="paragraph_small_bold" $color="N900" />
          {user?.email ? <Text $text={escapeHtml(user.email)} $variant="caption_medium" $color="N400" /> : null}
        </Heading>
      );
    },
  },
  {
    id: "answer",
    header: "Resposta",
    size: 520,
    cell: ({ row }) => <Answer>{answerText(row.original)}</Answer>,
  },
  {
    id: "date",
    header: "Respondido em",
    size: 180,
    cell: ({ row }) => (row.original.created_at ? formatDateTime(row.original.created_at) : "—"),
  },
];

function AnswersTable({ answers }) {
  return (
    <BasicTable
      data={answers}
      columns={answerColumns}
      getRowId={(row) => String(row.id)}
      noResults={<NoResults notResultText="Nenhuma resposta para esta pergunta" />}
    />
  );
}

function MultipleSummary({ question, answers }) {
  const summary = summarizeQuestion(question, answers);
  return (
    <Stack $gap="16px">
      {summary.options.map((option) => (
        <OptionRow key={option.title}>
          <ProgressBar
            $value={option.count}
            $max={summary.total || 1}
            $label={option.title}
            $valueText={countPercentLabel(option)}
          />
          <div>{option.isCorrect ? <StatePill $text="Correta" $variant="green" $size="small" /> : null}</div>
        </OptionRow>
      ))}
      {summary.others.length ? (
        <OptionRow>
          <ProgressBar
            $value={summary.others.reduce((sum, other) => sum + other.count, 0)}
            $max={summary.total || 1}
            $variant="gray"
            $label="Outras respostas"
            $valueText={countPercentLabel({
              count: summary.others.reduce((sum, other) => sum + other.count, 0),
              percent: summary.others.reduce((sum, other) => sum + other.percent, 0),
            })}
          />
          <div />
        </OptionRow>
      ) : null}
      {summary.others.length ? (
        <Text
          $text="Fora das opções atuais (a opção mudou depois da resposta): %s"
          $replaces={[summary.others.map((other) => `“${escapeHtml(other.title)}” (${other.count})`).join(", ")]}
          $variant="caption_medium"
          $color="N400"
        />
      ) : null}
    </Stack>
  );
}

function QuestionBox({ question, index, answers }) {
  return (
    <ContentBox>
      <Stack>
        <Heading>
          <Text
            $text={`Pergunta ${index + 1} · ${isMultiple(question) ? "Múltipla escolha" : "Escrita"} · ${answersLabel(answers.length)}`}
            $variant="caption_medium"
            $color="N400"
          />
          <Text $text={escapeHtml(question.description || "")} $variant="h4_bold" $color="N900" />
        </Heading>
        {isMultiple(question) ? (
          <MultipleSummary question={question} answers={answers} />
        ) : (
          <AnswersTable answers={answers} />
        )}
      </Stack>
    </ContentBox>
  );
}

export default function QuizAnswers() {
  const { id } = useParams();
  const quizService = useService("quiz");
  const quizAnswersService = useService("quizAnswers");
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);

  // O Layout escapa o título
  usePageHeader({
    title: quiz ? quiz.title || "Questionário" : "Respostas",
    breadcrumb: [
      { page: "Questionários", route: LIST_HREF },
      { page: "Respostas", route: `${LIST_HREF}/${id}/answers` },
    ],
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function fetchData() {
    try {
      const [quizResult, answersResult] = await Promise.all([
        quizService.get(id),
        quizAnswersService.listAll(`quiz_id=${id}&limit=9999`),
      ]);
      if (!quizResult?.data) throw new Error("Questionário não encontrado");
      if (!Array.isArray(answersResult?.data)) throw new Error("Erro ao carregar as respostas");
      setQuiz({ ...quizResult.data, questions: sortQuestions(quizResult.data.questions) });
      setAnswers(answersResult.data);
    } catch (error) {
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao carregar as respostas");
      navigate(LIST_HREF);
    }
  }

  if (!quiz) return <AnswersSkeleton />;

  const { byQuestion, orphans } = groupAnswers(quiz.questions, answers);
  const respondents = new Set(answers.map((answer) => answer.created_by)).size;

  return (
    <ContentPage>
      <PageActions>
        <Button $text="Editar questionário" $variant="outline" $action={() => navigate(`${LIST_HREF}/${id}`)} />
      </PageActions>

      <ContentBox>
        <DescriptionList
          $title="Questionário"
          $columns={3}
          $items={[
            { label: "Respostas", value: respondentsLabel(respondents) },
            { label: "Perguntas", value: questionsLabel(quiz.questions.length) },
            { label: "Status", value: <DomainStatus domain="questionario" value={quizStatusKey(quiz.status)} /> },
          ]}
        />
      </ContentBox>

      {answers.length === 0 ? (
        <ContentBox>
          <NoResults notResultText="Ninguém respondeu ainda" />
        </ContentBox>
      ) : (
        <>
          {quiz.questions.map((question, index) => (
            <QuestionBox key={question.id} question={question} index={index} answers={byQuestion[question.id] || []} />
          ))}
          {orphans.length ? (
            <ContentBox>
              <Stack>
                <Heading>
                  <Text $text={answersLabel(orphans.length)} $variant="caption_medium" $color="N400" />
                  <Text $text="Respostas sem pergunta" $variant="h4_bold" $color="N900" />
                </Heading>
                <Notice
                  $variant="gray"
                  $text="Estas respostas foram gravadas antes de um ajuste no sistema e não ficaram ligadas a nenhuma pergunta."
                />
                <AnswersTable answers={orphans} />
              </Stack>
            </ContentBox>
          ) : null}
        </>
      )}
    </ContentPage>
  );
}
